import React, { useState } from 'react';
import { Search, X, Plus } from 'lucide-react';

const CustomerSection = ({
  selectedCustomer,
  customerSearch,
  setCustomerSearch,
  customerResults,
  showCustomerDropdown,
  searchCustomers,
  handleCustomerSelect,
  clearCustomerSelection,
  onAddNewCustomer,
  formData,
  updateFormField
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    searchCustomers(value);
    if (value.length > 0) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  };

  const handleCustomerSelectWithModal = (customer) => {
    handleCustomerSelect(customer);
    setShowModal(false);
  };

  const handleAddNew = () => {
    setShowModal(false);
    if (onAddNewCustomer) {
      onAddNewCustomer();
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden relative">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-pest-blue">Treatment Contact</h3>
      </div>

      <div className="p-6 space-y-4">
        {/* Customer search / selection */}
        {selectedCustomer ? (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg border border-blue-200 flex justify-between items-center">
            <div>
              <p className="text-sm font-semibold text-blue-900">Billing Customer: {selectedCustomer.name}</p>
              {selectedCustomer.email && <p className="text-xs text-blue-700">{selectedCustomer.email}</p>}
            </div>
            <button
              type="button"
              onClick={clearCustomerSelection}
              className="text-blue-400 hover:text-red-600 transition-colors p-1"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10" />
              <input
                type="text"
                placeholder="Search customers by name, email, or phone..."
                value={customerSearch}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
              />
            </div>
            <button
              type="button"
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pest-green to-green-600 text-white rounded-lg font-semibold text-sm hover:from-green-600 hover:to-green-700 transition-all"
            >
              <Plus size={16} />
              Add New Customer
            </button>
          </div>
        )}

        {/* Treatment Contact Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
            <div className="relative">
              <input
                type="text"
                value={formData.treatment_contact_name}
                onChange={(e) => updateFormField('treatment_contact_name', e.target.value)}
                className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                placeholder="Contact name"
              />
              {formData.treatment_contact_name && (
                <button type="button" onClick={() => updateFormField('treatment_contact_name', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <div className="relative">
              <input
                type="text" inputMode="tel"
                value={formData.treatment_contact_phone}
                onChange={(e) => updateFormField('treatment_contact_phone', e.target.value)}
                className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                placeholder="Contact phone"
              />
              {formData.treatment_contact_phone && (
                <button type="button" onClick={() => updateFormField('treatment_contact_phone', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
          <div className="relative">
            <input
              type="text"
              value={formData.treatment_contact_company}
              onChange={(e) => updateFormField('treatment_contact_company', e.target.value)}
              className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
              placeholder="Company name"
            />
            {formData.treatment_contact_company && (
              <button type="button" onClick={() => updateFormField('treatment_contact_company', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
          <div className="relative">
            <input
              type="email"
              value={formData.treatment_contact_email}
              onChange={(e) => updateFormField('treatment_contact_email', e.target.value)}
              className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
              placeholder="Contact email"
            />
            {formData.treatment_contact_email && (
              <button type="button" onClick={() => updateFormField('treatment_contact_email', '')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
            )}
          </div>
        </div>
      </div>

      {/* Customer Search Modal */}
      {showModal && customerSearch.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-60 overflow-hidden overscroll-contain"
          onClick={handleModalClose}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden m-4 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
              <h3 className="text-lg font-semibold text-white">Search Results</h3>
              <button
                onClick={handleModalClose}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search customers by name, email, or phone..."
                  value={customerSearch}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {customerResults.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {customerResults.map(customer => (
                    <button
                      key={customer.id}
                      type="button"
                      onClick={() => handleCustomerSelectWithModal(customer)}
                      className="w-full text-left px-4 py-3 hover:bg-pest-teal/10 transition-colors duration-200"
                    >
                      <div className="font-medium text-gray-900">{customer.name}</div>
                      {customer.email && (
                        <div className="text-sm text-gray-600">{customer.email}</div>
                      )}
                      {customer.phone && (
                        <div className="text-sm text-gray-600">{customer.phone}</div>
                      )}
                      {customer.address && (
                        <div className="text-sm text-gray-500 mt-1">{customer.address}</div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p>No customers found matching "{customerSearch}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSection;
